import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';

/**
 * Per-user preferences — mirrors server/userPreferences.ts (the registry and its defaults live there).
 * The last values seen are mirrored to localStorage (`cosmos:prefs`) so the shell renders in the right mode
 * on first paint, before the request returns; the server wins once loaded.
 */

export type SidebarMode = 'follow' | 'keep' | 'single';
export type Density = 'comfortable' | 'compact';

export interface PreferenceValues {
  'nav.sidebarMode': SidebarMode;
  'general.landingPage': string;
  'display.density': Density;
}
export type PreferenceKey = keyof PreferenceValues;

/** First-paint fallbacks only — keep in step with the server registry's defaults. */
const FALLBACK: PreferenceValues = {
  'nav.sidebarMode': 'follow',
  'general.landingPage': '/',
  'display.density': 'comfortable',
};

export type PreferenceDef =
  | { key: PreferenceKey; group: string; label: string; description: string; type: 'select'; default: string; options: { value: string; label: string; description?: string }[] }
  | { key: PreferenceKey; group: string; label: string; description: string; type: 'path'; default: string };

type Payload = { defs: PreferenceDef[]; values: PreferenceValues };

const KEY = ['preferences'];
const MIRROR_KEY = 'cosmos:prefs';

function readMirror(): PreferenceValues {
  try { return { ...FALLBACK, ...(JSON.parse(localStorage.getItem(MIRROR_KEY) ?? '{}') as Partial<PreferenceValues>) }; } catch { return FALLBACK; }
}
/** Parsed once per page load and kept in step by storeMirror() — not re-parsed on every render. */
let mirror: PreferenceValues | null = null;
function loadMirror(): PreferenceValues {
  return (mirror ??= readMirror());
}
function storeMirror(v: PreferenceValues) {
  mirror = { ...FALLBACK, ...v };
  try { localStorage.setItem(MIRROR_KEY, JSON.stringify(v)); } catch { /* storage unavailable */ }
}

/** Read synchronously from the mirror, for code that runs before React (or outside it). */
export const mirroredPreference = <K extends PreferenceKey>(key: K): PreferenceValues[K] => loadMirror()[key];

export function usePreferences() {
  return useQuery({
    queryKey: KEY,
    queryFn: async () => { const r = await api.get<Payload>('/api/preferences'); storeMirror(r.values); return r; },
    staleTime: 10 * 60_000,
  });
}

/** One preference's value: the server's once loaded, the localStorage mirror until then. */
export function usePreference<K extends PreferenceKey>(key: K): PreferenceValues[K] {
  const { data } = usePreferences();
  return (data?.values ?? loadMirror())[key];
}

/** Every mutation returns the full set, so the cache (and mirror) is replaced rather than refetched. */
function usePrefMutation<V>(fn: (v: V) => Promise<Payload>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: (r) => { storeMirror(r.values); qc.setQueryData(KEY, r); },
  });
}

export const useSetPreference = () =>
  usePrefMutation(({ key, value }: { key: PreferenceKey; value: string }) =>
    api.put<Payload>(`/api/preferences/${encodeURIComponent(key)}`, { value }));
export const useResetPreference = () =>
  usePrefMutation((key: PreferenceKey) => api.del<Payload>(`/api/preferences/${encodeURIComponent(key)}`));
